import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2, Facebook, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { API } from "../data/api";

const ease = [0.16, 1, 0.3, 1];

const SERVICE_OPTIONS = [
  "Infissi in Alluminio",
  "Lavorazione del Ferro",
  "Infissi in PVC",
  "Cancelli & Portoni",
  "Zanzariere",
  "Tapparelle & Avvolgibili",
  "Porte & Vetrate",
  "Altro",
];

export const Contact = () => {
  const { content } = useContent();
  const COMPANY = content.company;
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "", privacy: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Inserisci nome e telefono per favore.");
      return;
    }
    if (!form.privacy) {
      toast.error("Devi accettare l'informativa sulla privacy.");
      return;
    }
    setLoading(true);
    try {
      const { privacy, ...payload } = form;
      await axios.post(`${API}/quotes`, payload);
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatteremo a breve.");
      setForm({ name: "", phone: "", email: "", service: "", message: "", privacy: false });
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o chiamaci.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-navy/20 py-3 text-navy placeholder:text-navy/35 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 md:py-36 bg-paper">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Left: info */}
        <div>
          <p className="eyebrow mb-5">Contatti</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight text-navy">
            Parliamo del tuo progetto.
          </h2>
          <p className="mt-6 text-navy/65 leading-relaxed max-w-md">
            Raccontaci cosa hai in mente: ti offriamo un sopralluogo e un
            preventivo gratuito, senza impegno.
          </p>

          <div className="mt-12 space-y-7">
            <a href={`tel:${COMPANY.phoneHref}`} data-testid="contact-phone" className="flex items-start gap-4 group">
              <Phone className="text-gold mt-1 shrink-0" size={20} />
              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.22em] text-navy/45">Telefono</p>
                <p className="text-navy text-lg group-hover:text-gold transition-colors">{COMPANY.phone}</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="text-gold mt-1 shrink-0" size={20} />
              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.22em] text-navy/45">Officina</p>
                <p className="text-navy text-lg">{COMPANY.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="text-gold mt-1 shrink-0" size={20} />
              <div>
                <p className="text-[0.62rem] uppercase tracking-[0.22em] text-navy/45">Orari</p>
                {COMPANY.hours.map((h) => (
                  <p key={h.d} className="text-navy/80">
                    <span className="text-navy">{h.d}:</span> {h.h}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <a
            href={`https://wa.me/${COMPANY.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp"
            className="mt-10 inline-flex items-center gap-3 bg-navy text-paper px-7 py-4 text-xs uppercase tracking-[0.2em] hover:bg-gold transition-colors duration-400"
          >
            Scrivici su WhatsApp
          </a>

          <div className="mt-8 flex items-center gap-4">
            <span className="text-[0.62rem] uppercase tracking-[0.22em] text-navy/45">Seguici</span>
            <a href={COMPANY.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="w-11 h-11 flex items-center justify-center border border-navy/20 text-navy hover:bg-navy hover:text-paper transition-all duration-300">
              <Facebook size={18} />
            </a>
            <a href={COMPANY.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="w-11 h-11 flex items-center justify-center border border-navy/20 text-navy hover:bg-navy hover:text-paper transition-all duration-300">
              <Instagram size={18} />
            </a>
          </div>
        </div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="bg-white border border-gold/20 p-8 md:p-12"
        >
          {done ? (
            <div data-testid="contact-success" className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="text-gold" size={56} />
              <h3 className="font-serif text-3xl text-navy mt-6">Grazie!</h3>
              <p className="text-navy/65 mt-3 max-w-xs">
                La tua richiesta è stata inviata. Ti ricontatteremo al più presto.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 text-xs uppercase tracking-[0.2em] text-gold link-underline"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="space-y-7">
              <p className="eyebrow">Preventivo gratuito</p>
              <div className="grid sm:grid-cols-2 gap-7">
                <input data-testid="input-name" name="name" value={form.name} onChange={onChange} placeholder="Nome e cognome *" className={inputCls} />
                <input data-testid="input-phone" name="phone" value={form.phone} onChange={onChange} placeholder="Telefono *" className={inputCls} />
              </div>
              <input data-testid="input-email" type="email" name="email" value={form.email} onChange={onChange} placeholder="Email" className={inputCls} />
              <div className="relative">
                <select
                  data-testid="input-service"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className={`${inputCls} appearance-none ${form.service ? "text-navy" : "text-navy/35"}`}
                >
                  <option value="">Servizio di interesse</option>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="text-navy">{o}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="input-message" name="message" value={form.message} onChange={onChange} rows={4} placeholder="Descrivi il tuo progetto" className={`${inputCls} resize-none`} />
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  data-testid="input-privacy"
                  type="checkbox"
                  name="privacy"
                  checked={form.privacy}
                  onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#C5A059] shrink-0"
                />
                <span className="text-xs text-navy/60 leading-relaxed">
                  Ho letto e accetto l'{" "}
                  <Link to="/privacy" className="text-gold underline underline-offset-2 hover:text-gold-dark">informativa sulla privacy</Link>{" "}
                  e acconsento al trattamento dei miei dati per rispondere alla richiesta. *
                </span>
              </label>
              <button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-navy text-paper py-4 text-xs uppercase tracking-[0.22em] hover:bg-gold transition-colors duration-400 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={16} /> Invio…</> : <><Send size={15} /> Invia richiesta</>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
